import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import type { GetStaticProps } from 'next';
import Page from '@/components/Page/Page';
import { type PageContent, PageContentType } from '@/contentful';
import { getPageContentByPath } from '@/contentful/api/contentfulClientApi';
import { getSunrunShopProductPageContentByPath } from '@/contentful/api/graphQL/sunrun-shop/productPage';
import { loadSyncCache } from '@/contentful/scripts/prefetchContent/loadContentfulCache';
import { isBlogCategoryType } from '@/utils/blog';
import { NextOutput } from '@/utils/nextOutput';
import { normalizePath } from '@/utils/paths/normalizePath';

interface Props {
  pageContent: PageContent;
  preview: boolean;
}

export default function Path({ pageContent, preview }: Props) {
  let updatedPageContent = pageContent;
  if (preview && !isBlogCategoryType(pageContent.fields.pageContent)) {
    // biome-ignore lint/correctness/useHookAtTopLevel: Automated migration to Biome
    updatedPageContent = useContentfulLiveUpdates(pageContent);
  }

  return (
    <>
      {/** biome-ignore lint/correctness/useUniqueElementIds: Automated migration to Biome */}
      <div className='blankDiv' id='blankDiv'></div>
      <Page content={updatedPageContent} />
    </>
  );
}

export const getStaticPaths = async () => {
  const isAutomationMode = process.env.NEXT_OUTPUT === NextOutput.AUTOMATION;
  const isPreviewMode = process.env.NEXT_OUTPUT === NextOutput.SERVER;

  if (isPreviewMode) {
    return { paths: [], fallback: 'blocking' };
  }

  const cached = loadSyncCache();
  if (!cached) {
    throw Error('No Caches was present during build');
  }
  const keys = Object.keys(cached);

  const paths = keys
    .map((item) => {
      const segments = item === '/' ? [] : item.slice(1).split('/');
      return { params: { path: segments } };
    })
    .filter((p): p is { params: { path: string[] } } => p !== null);

  console.log(`[cache] Generated ${paths.length} static paths`);

  if (isAutomationMode) {
    const automationPaths = paths.filter(({ params }) => {
      const segments = params.path;
      return segments[0] === 'test' && segments[1] === 'automation';
    });
    return { paths: automationPaths, fallback: false };
  }

  return { paths, fallback: false };
};

export const getStaticProps: GetStaticProps = async ({ params, preview }) => {
  const requestedPath = Array.isArray(params?.path)
    ? `/${params.path.join('/')}`
    : '/';

  // fetch fresh content outside of actions build and when preview mode is true
  const isPreviewServer = process.env.IS_PREVIEW_SERVER === 'true';
  const isCI = process.env.IS_GH_ACTIONS_BUILD === 'true';
  if (isPreviewServer && !isCI) {
    const entry: PageContent = await getPageContentByPath(requestedPath, true);

    return {
      props: {
        pageContent: entry,
        preview: true,
      },
      // required so server doesn't cache content (Reset cache 1 sec)
      revalidate: 1,
    };
  }

  const cached = loadSyncCache();
  if (!cached) {
    throw Error('No Caches was present during build');
  }

  const match = cached[normalizePath(requestedPath)];
  if (!match?.entry) {
    console.error(`[cache] No match found for path ${requestedPath}`);
    return { notFound: true };
  }

  const pageContent = match.entry as PageContent;

  const subType = pageContent?.fields?.pageContent?.sys?.contentType?.sys?.id as
    | PageContentType
    | undefined;

  if (subType === PageContentType.SUNRUN_SHOP_PRODUCT) {
    console.log(`[shop] Fetching fresh GraphQL data for ${requestedPath}`);
    const freshShopData = await getSunrunShopProductPageContentByPath(
      requestedPath,
      preview
    );

    if (!freshShopData) {
      console.warn(`[shop] No data found for ${requestedPath}`);
      return { notFound: true };
    }

    return {
      props: {
        pageContent: freshShopData,
        preview: preview || false,
      },
    };
  }

  if (!pageContent?.fields) {
    console.error(`[cache] Invalid or missing fields for ${requestedPath}`);
    return { notFound: true };
  }

  return {
    props: {
      pageContent,
      preview: preview || false,
    },
  };
};
